// League Almanac report — full data archive of the user's league.
//
// After the AI strip-down: no LLM trivia generation, no preview bullets,
// no hero bullets section. The data IS the product. The upstream pipeline
// (Sleeper/Yahoo fetcher → adapters → computeFunStats) produces the stats
// object in RecapImport; this component is pure rendering, mounts instantly
// once the EmailGate unlocks — no more 30-60s trivia wait.
//
// Mobile-first: each table has two render paths gated by a @media query —
// real <table> on desktop (≥768px), stacked label-value cards on mobile.
// Collapsibles render a deterministic one-line highlight in the header
// (src/engine/sectionHighlights.js) so the reader has a teaser before
// expanding.

import { useState, useEffect, useRef, useMemo } from "react";
import theme from "../../data/theme";
import { pickSectionHighlight } from "../../engine/sectionHighlights";
import DraftBoardGrid from "./DraftBoardGrid";
import AlmanacSidebar from "./AlmanacSidebar";
import AlmanacHero from "./AlmanacHero";
import FeaturedCardsRow from "./FeaturedCardsRow";
import CareerTotalsCard from "./CareerTotalsCard";
import LineupView from "./LineupView";
import { computeLeagueEvolution, collectKeeperHistory } from "../../engine/leagueEvolution";
import { buildAlmanacExport } from "../../utils/buildAlmanacExport";
import { downloadJson, slugify } from "../../utils/downloadJson";
import {
  captureCardUrlCopied,
  captureCardDownloaded,
  captureSectionView,
  captureAlmanacJsonDownloaded,
  captureAlmanacSharePublished,
  captureAlmanacShareFailed,
} from "../../utils/recapAnalytics";
// Below this many regular-season matchups, every stat table is thin or
// empty. Show an explicit "come back later" empty-state instead of half-
// rendering. Common case: commissioner trying it on a brand-new league
// after 1-2 weeks.
const MIN_MATCHUPS_FOR_REPORT = 30;

export default function RecapReport({ pipelineState }) {
  // Collapsible-section state lifted from <Sections> so the sidebar (which
  // sits OUTSIDE <Sections>) can also call open/scroll. Used by both
  // <Sections> for the section toggles and <AlmanacSidebar> for jump-link
  // clicks.
  const [openSections, setOpenSections] = useState(() => new Set());
  const toggleSection = (s) => setOpenSections((prev) => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });
  const openAndScrollTo = (s) => {
    setOpenSections((prev) => {
      if (prev.has(s)) return prev;
      const next = new Set(prev);
      next.add(s);
      return next;
    });
    // requestAnimationFrame so the section has finished expanding before
    // scrollIntoView runs — otherwise the scroll target jumps as the body
    // height grows mid-animation.
    requestAnimationFrame(() => {
      const el = typeof document !== "undefined"
        ? document.getElementById(`recap-section-${s}`) : null;
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const totalMatchups = pipelineState?.stats?.meta?.totalMatchups ?? 0;
  if (totalMatchups < MIN_MATCHUPS_FOR_REPORT) {
    return <InsufficientDataState pipelineState={pipelineState} matchups={totalMatchups} />;
  }

  // Which sections actually render (Drafts/Trades are conditional, and
  // Format History only fires when there are real diffs to show). The
  // sidebar uses this map to skip dead links.
  const stats = pipelineState?.stats;
  // Compute evolution once at the top level so the sidebar and the section
  // renderer share the same instance (the diff walk is cheap but
  // duplicating allocations would be wasteful).
  const evolution = computeLeagueEvolution(
    pipelineState?.seasons || [],
    pipelineState?.platform,
    stats?.drafts || []
  );
  const keeperHistory = collectKeeperHistory(stats?.drafts || []);
  const availableSections = {
    drafts: Array.isArray(stats?.drafts) && stats.drafts.length > 0,
    trades: Array.isArray(stats?.trades) && stats.trades.length > 0,
    keepers: keeperHistory.hasKeepers,
    formatHistory: evolution.hasContent,
  };
  const years = pipelineState?.leagueMetadata?.yearsCovered || [];
  const yearRange = years.length ? `${years[0]}–${years[years.length - 1]}` : "";

  // downloadButton stays a NODE prop — the sidebar is handed a rendered CTA
  // block and stays ignorant of pipelineState. A fragment is all it takes to
  // put Share beside Download there.
  const sidebar = (
    <AlmanacSidebar
      leagueName={pipelineState?.leagueMetadata?.name}
      yearRange={yearRange}
      availableSections={availableSections}
      onJumpToSection={openAndScrollTo}
      downloadButton={(
        <>
          <AlmanacDownloadButton pipelineState={pipelineState} compact />
          <AlmanacShareButton pipelineState={pipelineState} compact />
        </>
      )}
    />
  );

  return (
    <ReportShell pipelineState={pipelineState} sidebar={sidebar}>
      <Sections
        pipelineState={pipelineState}
        openSections={openSections}
        toggleSection={toggleSection}
        evolution={evolution}
        keeperHistory={keeperHistory}
      />
    </ReportShell>
  );
}

// ============================================================================
// SHARE CARDS GALLERY — three data-derived leaderboards
// ============================================================================
//
// Post AI strip-down: no bullet cards. Just the three deterministic
// leaderboard cards (Title Leaderboard, All-Time Wins, Biggest Blowout).
// Each card has Copy URL + Download buttons — the URL is what gets
// pasted into Discord/Slack/iMessage to unfurl as an image. Server-side
// PNG rendering at /api/recap/share-card is unchanged.

function ShareCardsGallery({ pipelineState }) {
  const league = pipelineState?.leagueMetadata?.name || "Your League";
  const yearsArr = pipelineState?.leagueMetadata?.yearsCovered || [];
  const years = yearsArr.length ? `${yearsArr[0]}-${yearsArr[yearsArr.length - 1]}` : "";

  const leaderCards = useMemo(
    () => buildLeaderboardCards(pipelineState),
    [pipelineState]
  );

  if (leaderCards.length === 0) return null;

  return (
    <section id="recap-section-shareCards" className="recap-section">
      <h2 style={{
        fontSize: 22, margin: "0 0 8px", color: theme.colors.text.primary,
        fontWeight: theme.fontWeight.black, letterSpacing: "-0.5px",
      }}>Share Cards</h2>
      <div style={{
        fontSize: 13, color: theme.colors.text.muted, fontFamily: theme.fonts.body,
        marginBottom: 16,
      }}>
        1080×1080 PNGs ready for iMessage / Slack / Discord / Twitter. Copy the
        URL to unfurl as an image, or download the PNG directly. Tagged "from
        League Almanac" in the footer.
      </div>
      <div className="recap-cards-grid">
        {leaderCards.map((c) => (
          <CardThumb
            key={c.key}
            cardUrl={buildCardUrl(c, league, years)}
            label={c.label}
            cardType={c.type}
          />
        ))}
      </div>
    </section>
  );
}

function CardThumb({ cardUrl, label, cardType, bulletId }) {
  const [copied, setCopied] = useState(false);
  const absoluteUrl = typeof window !== "undefined" ? `${window.location.origin}${cardUrl}` : cardUrl;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      captureCardUrlCopied({ cardType, bulletIdIfApplicable: bulletId });
    } catch { /* clipboard unavailable */ }
  };
  const onDownload = () => {
    captureCardDownloaded({ cardType, bulletIdIfApplicable: bulletId });
    // anchor's `download` attribute handles the actual file save; we just
    // capture the intent before navigation kicks off
  };
  return (
    <div style={{
      background: theme.colors.bg.raised,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 12, overflow: "hidden",
      display: "flex", flexDirection: "column",
    }}>
      <img
        src={cardUrl}
        alt={label}
        loading="lazy"
        style={{ width: "100%", aspectRatio: "1 / 1", display: "block", background: theme.colors.bg.surface }}
      />
      <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{
          fontSize: 10, color: theme.colors.text.muted, fontFamily: theme.fonts.display,
          letterSpacing: 1.5, textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
        }}>{label}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={onCopy}
            style={{
              flex: 1,
              background: copied ? theme.colors.status.success : "transparent",
              color: copied ? "#fff" : theme.colors.text.primary,
              border: `1px solid ${copied ? theme.colors.status.success : theme.colors.border.default}`,
              borderRadius: 6, padding: "6px 10px",
              fontSize: 11, fontWeight: theme.fontWeight.heavy, letterSpacing: 1,
              fontFamily: theme.fonts.display, cursor: "pointer", textTransform: "uppercase",
            }}
          >{copied ? "Copied URL" : "Copy URL"}</button>
          <a
            href={cardUrl}
            download
            onClick={onDownload}
            style={{
              flex: 1, textAlign: "center",
              background: theme.colors.brand.gradientBtn, color: "#fff", textDecoration: "none",
              borderRadius: 6, padding: "6px 10px",
              fontSize: 11, fontWeight: theme.fontWeight.heavy, letterSpacing: 1,
              fontFamily: theme.fonts.display, textTransform: "uppercase",
            }}
          >Download</a>
        </div>
      </div>
    </div>
  );
}

function buildLeaderboardCards(pipelineState) {
  const stats = pipelineState?.stats;
  if (!stats) return [];
  const cards = [];

  // Championships — show every owner with at least one title. PNG renderer
  // applies adaptive font sizing for >10 rows; no truncation here so a
  // 10-team Smithtown sees all 10 (not 6).
  const titleRows = Object.entries(stats.championships?.titleCounts || {})
    .map(([owner, years]) => ({ owner, count: years.length }))
    .sort((a, b) => b.count - a.count);
  if (titleRows.length >= 2) {
    cards.push({ type: "championships", label: "Title Leaderboard", data: titleRows, key: "champs" });
  }

  // All-time wins — every owner, sorted by total record (== H2H when no
  // median format). PNG renderer handles tall lists with smaller font.
  const winRows = (stats.careerTotals || [])
    .map((c) => ({ owner: c.owner, w: c.totalW ?? c.w, l: c.totalL ?? c.l }));
  if (winRows.length >= 2) {
    cards.push({ type: "wins", label: "All-Time Wins", data: winRows, key: "wins" });
  }

  // Biggest blowout
  const bb = stats.singleGame?.biggestBlowouts?.[0];
  if (bb) {
    cards.push({
      type: "blowout",
      label: "Biggest Blowout",
      key: "blowout",
      data: {
        winner: bb.owner, winnerPts: bb.pts,
        loser: bb.opp, loserPts: bb.oppPts,
        margin: bb.margin, year: bb.year, week: bb.week,
      },
    });
  }

  return cards;
}

function buildCardUrl(card, league, years) {
  const params = new URLSearchParams({ type: card.type, league, years });
  if (card.type === "bullet") {
    params.set("text", card.text);
  } else if (card.data != null) {
    params.set("data", JSON.stringify(card.data));
  }
  return `/api/recap/share-card?${params.toString()}`;
}

// ============================================================================
// SHELL / HEADER
// ============================================================================

function ReportShell({ pipelineState, sidebar, children }) {
  const name = pipelineState?.leagueMetadata?.name || "Your League";
  const years = pipelineState?.leagueMetadata?.yearsCovered || [];
  const yearRange = years.length ? `${years[0]}–${years[years.length - 1]}` : "";
  return (
    <div className="recap-report" style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      fontFamily: theme.fonts.display,
      paddingBottom: 80,
    }}>
      <div className="recap-page">
        {sidebar}
        <div className="recap-content">
      <header style={{
        padding: "32px 20px 24px",
        textAlign: "center",
      }}>
        <div style={{
          fontSize: 11, letterSpacing: 3, color: theme.colors.brand.accent,
          textTransform: "uppercase", fontWeight: theme.fontWeight.heavy, marginBottom: 6,
        }}>League Almanac {yearRange}</div>
        <h1 style={{
          fontSize: 36, margin: 0, fontWeight: theme.fontWeight.black,
          color: theme.colors.text.primary, letterSpacing: "-1px",
        }}>{name}</h1>
        <div style={{
          color: theme.colors.text.muted, fontSize: 13,
          fontFamily: theme.fonts.body, marginTop: 6,
        }}>
          {pipelineState?.stats?.meta?.totalOwners || 0} owners · {pipelineState?.stats?.meta?.totalMatchups || 0} regular-season matchups
        </div>
        {pipelineState?.stats?.meta?.formatHasMedianWins && (
          <div style={{
            display: "inline-block",
            marginTop: 10,
            padding: "4px 10px",
            borderRadius: 6,
            background: "rgba(34, 197, 94, 0.15)",
            border: "1px solid rgba(34, 197, 94, 0.5)",
            color: "#86efac",
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            fontWeight: theme.fontWeight.heavy,
          }}
          title={
            pipelineState.stats.meta.formatMedianAllYears
              ? "This league awards an extra weekly win to teams scoring above the league median. All W-L totals reflect both H2H and median wins."
              : `Median match was adopted in ${pipelineState.stats.meta.formatMedianFirstYear}. Records from earlier seasons remain H2H-only; later seasons include both H2H and median wins. Hover any cell to see the breakdown.`
          }
          >
            {pipelineState.stats.meta.formatMedianAllYears
              ? "Median Wins Format"
              : `Median Wins (since ${pipelineState.stats.meta.formatMedianFirstYear})`}
          </div>
        )}
        {/* Hidden ≥1280px by .almanac-download-btn--header-wrapper, where the
            sidebar rail carries both CTAs instead. One of each at any
            breakpoint, no duplication. */}
        <div className="almanac-download-btn--header-wrapper">
          <AlmanacDownloadButton pipelineState={pipelineState} />
          <AlmanacShareButton pipelineState={pipelineState} />
        </div>
      </header>
      <div style={{ padding: "0 16px" }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

// Task #41 — "Download JSON" CTA in the report header. Builds the
// almanac.v1 payload synchronously from in-memory pipelineState (no I/O,
// no API calls — the data is already loaded). For 20-year leagues the
// payload is ~1-2 MB; serialization completes in <100ms on modern devices.
//
// The button is also exported separately for the upcoming sidebar nav rail
// (task #39), which will reuse the same handler at the bottom of the rail.
function AlmanacDownloadButton({ pipelineState, compact }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const handleDownload = () => {
    if (!pipelineState?.stats) return;
    setBusy(true);
    try {
      const payload = buildAlmanacExport(pipelineState);
      const json = JSON.stringify(payload, null, 2);
      const fileSizeKb = Math.round(json.length / 1024);
      const years = pipelineState.stats.meta?.yearsCovered || [];
      const yearTag = years.length
        ? `${years[0]}-${years[years.length - 1]}`
        : "almanac";
      const filename = `${slugify(pipelineState.leagueMetadata?.name)}_almanac_${yearTag}.json`;
      downloadJson(payload, filename);
      captureAlmanacJsonDownloaded({
        platform: pipelineState.platform,
        seasonCount: pipelineState.seasons?.length || 0,
        fileSizeKb,
      });
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: compact ? 0 : 14 }}>
      <button
        type="button"
        onClick={handleDownload}
        disabled={busy || !pipelineState?.stats}
        className={`almanac-download-btn${compact ? " almanac-download-btn--compact" : ""}`}
      >
        {done ? "✓ Downloaded" : busy ? "Building…" : "↓ Download JSON"}
      </button>
    </div>
  );
}

// Share link — the same almanac.v1 payload the download button builds, POSTed
// to api/almanac-share and handed back as a URL. The server compresses and
// stores it; the id is the capability, so the clipboard write IS the feature.
//
// 🔴 THIS MUST NEVER DEGRADE THE REPORT. Publishing is an affordance bolted on
// top of a report that is already complete and already the user's — exactly
// like the download button. Every failure path below terminates in local state
// on this component and nothing else: no throw escapes the handler, no parent
// state is touched, no section closes.
//
// The clipboard is the one step that can fail AFTER a successful publish (iOS
// Safari and some Android WebViews reject writeText outside a user-gesture
// microtask, and a permissions policy can deny it outright). That is NOT an
// error — the link exists and works. It falls back to a selected read-only
// input so the user copies by hand, and the button still reads as success.
function AlmanacShareButton({ pipelineState, compact }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [manualUrl, setManualUrl] = useState(null);
  const doneTimer = useRef(null);
  const manualRef = useRef(null);

  // The 1.8s revert must not fire into an unmounted component — a user can
  // navigate away mid-publish.
  useEffect(() => () => clearTimeout(doneTimer.current), []);

  // Autoselect the fallback URL so "copy" is one keystroke, not a drag.
  useEffect(() => {
    if (manualUrl && manualRef.current) manualRef.current.select();
  }, [manualUrl]);

  const handleShare = async () => {
    if (!pipelineState?.stats) return;
    setBusy(true);
    setError(null);
    setManualUrl(null);
    let sizeKb = 0;
    try {
      const payload = buildAlmanacExport(pipelineState);
      const body = JSON.stringify({ shareId: crypto.randomUUID(), export: payload });
      sizeKb = Math.round(body.length / 1024);

      const res = await fetch("/api/almanac-share", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
      });
      // A 413 from the platform edge (rather than our handler) has no JSON
      // body at all, so an unparseable response is a status, not a crash.
      let data = null;
      try { data = await res.json(); } catch { /* falls through to the status */ }
      if (!res.ok || !data?.ok) {
        // Surface the server's own message verbatim — it names which check
        // tripped ("too large to share yet", "Daily share limit reached"),
        // and rewriting it here would lose that.
        throw new Error(data?.error || `Publish failed (${res.status})`);
      }

      const url = `${window.location.origin}/almanac?share=${data.shareId}`;
      captureAlmanacSharePublished({
        platform: pipelineState.platform,
        seasonCount: pipelineState.seasons?.length || 0,
        sizeKb,
      });

      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Published fine, clipboard refused. Show the URL instead of an error.
        setManualUrl(url);
      }
      setDone(true);
      doneTimer.current = setTimeout(() => setDone(false), 1800);
    } catch (err) {
      const reason = err?.message || "Could not publish the share link.";
      setError(reason);
      captureAlmanacShareFailed({ reason, sizeKb });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: compact ? 8 : 14 }}>
      <button
        type="button"
        onClick={handleShare}
        disabled={busy || !pipelineState?.stats}
        className={`almanac-download-btn${compact ? " almanac-download-btn--compact" : ""}`}
      >
        {done ? "✓ Link copied" : busy ? "Publishing…" : "🔗 Share link"}
      </button>
      <div style={{
        fontFamily: theme.fonts.body,
        fontSize: 11,
        lineHeight: 1.4,
        color: theme.colors.text.muted,
        marginTop: 6,
      }}>
        Anyone with the link can view your league's almanac.
      </div>
      {manualUrl && (
        <input
          ref={manualRef}
          type="text"
          readOnly
          value={manualUrl}
          onFocus={(e) => e.target.select()}
          aria-label="Share link — copy this"
          style={{
            width: "100%", marginTop: 6, padding: "6px 8px",
            fontFamily: theme.fonts.body, fontSize: 11,
            color: theme.colors.text.primary,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.18)",
            borderRadius: 6, boxSizing: "border-box",
          }}
        />
      )}
      {error && (
        <div role="alert" style={{
          fontFamily: theme.fonts.body, fontSize: 11, lineHeight: 1.4,
          color: "#fca5a5", marginTop: 6,
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

// Friendly empty-state for leagues with <MIN_MATCHUPS_FOR_REPORT regular-
// season matchups. Common case: commissioner trying the product on a
// brand-new league after 1-2 weeks.
function InsufficientDataState({ pipelineState, matchups }) {
  const name = pipelineState?.leagueMetadata?.name || "Your league";
  return (
    <div style={{
      minHeight: "100vh",
      background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${theme.colors.bg.deep} 50%, ${theme.colors.bg.app} 100%)`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 40, fontFamily: theme.fonts.display, textAlign: "center",
    }}>
      <div style={{
        fontSize: 11, letterSpacing: 3, color: theme.colors.brand.accent,
        textTransform: "uppercase", fontWeight: theme.fontWeight.heavy, marginBottom: 8,
      }}>Not enough data yet</div>
      <h2 style={{
        fontSize: 28, margin: 0, fontWeight: theme.fontWeight.black,
        color: theme.colors.text.primary, letterSpacing: "-0.5px", maxWidth: 560,
      }}>{name} hasn't played enough games for a meaningful recap.</h2>
      <p style={{
        color: theme.colors.text.muted, fontFamily: theme.fonts.body, fontSize: 14,
        marginTop: 14, maxWidth: 460, lineHeight: 1.5,
      }}>
        Only {matchups} regular-season matchup{matchups !== 1 ? "s" : ""} on record. Come back at the end of the
        season for the full year-in-review.
      </p>
      <a href="/" style={{
        marginTop: 28, padding: "10px 22px",
        background: theme.colors.brand.gradientBtn, color: "#fff",
        textDecoration: "none", borderRadius: 10,
        fontSize: 12, fontWeight: theme.fontWeight.heavy, letterSpacing: 1.5,
        fontFamily: theme.fonts.display, textTransform: "uppercase",
      }}>Try the auction draft simulator →</a>
    </div>
  );
}

// ============================================================================
// SECTIONS — ordered for narrative flow per brief §1
// ============================================================================

function Sections({ pipelineState, openSections, toggleSection, evolution, keeperHistory }) {
  const stats = pipelineState?.stats;
  if (!stats) return null;
  const keepers = keeperHistory || { owners: [], totalKeepers: 0, seasonCount: 0, hasKeepers: false };

  const hasMedians = !!stats.meta?.formatHasMedianWins;
  const medianOpts = {
    show: hasMedians,
    firstYear: stats.meta?.formatMedianFirstYear || null,
    allYears: !!stats.meta?.formatMedianAllYears,
  };
  // evolution is computed once at the parent level and passed in to keep
  // the Sections renderer free of compute and the sidebar consistent.
  const formatEvolution = evolution || { formatChanges: [], seasonLabels: [], hasContent: false };

  // Per-section count chips ("Championships · 14 years tracked"). Empty
  // string hides the chip.
  const countFor = {
    championships: stats.championships?.years?.length
      ? `${stats.championships.years.length} ${stats.championships.years.length === 1 ? "year" : "years"} tracked` : "",
    drafts: Array.isArray(stats.drafts) ? `${stats.drafts.length} ${stats.drafts.length === 1 ? "draft" : "drafts"} archived` : "",
    keepers: keepers.hasKeepers ? `${keepers.totalKeepers} kept · ${keepers.seasonCount} ${keepers.seasonCount === 1 ? "season" : "seasons"}` : "",
    trades: stats.tradeMeta?.totalTrades
      ? `${stats.tradeMeta.totalTrades} ${stats.tradeMeta.totalTrades === 1 ? "trade" : "trades"}` : "",
    careerTotals: stats.meta?.totalOwners ? `${stats.meta.totalOwners} owners` : "",
    pointsPerGame: stats.careerTotals?.length ? `${stats.careerTotals.length} owners ranked` : "",
    formatHistory: formatEvolution.hasContent
      ? `${formatEvolution.formatChanges.length - 1} year${formatEvolution.formatChanges.length === 2 ? "" : "s"} of changes`
      : "",
    singleGame: stats.singleGame?.highest?.length ? `top ${stats.singleGame.highest.length}` : "",
    streaks: stats.streaks?.longestWinStreaks?.length ? `${stats.streaks.longestWinStreaks.filter(s => s.length > 0).length} owners ranked` : "",
    singleSeason: stats.singleSeason?.bestRecords?.length ? `${stats.singleSeason.bestRecords.length} top seasons` : "",
    headToHead: stats.headToHead?.lopsidedRivalries?.length != null
      ? `${stats.headToHead.lopsidedRivalries.length} lopsided rivalries` : "",
    playoffs: stats.playoffs?.careerRecords?.length ? `${stats.playoffs.careerRecords.length} playoff owners` : "",
    luck: stats.luck?.allPlay?.length ? `${stats.luck.allPlay.length} owners` : "",
    volatility: stats.volatility?.boomBust?.length ? `${stats.volatility.boomBust.length} owners` : "",
    yoySwings: stats.yoySwings?.biggestComebacks?.length || stats.yoySwings?.biggestFalls?.length
      ? "yearly comebacks & falls" : "",
    awards: "season awards",
  };

  const renderSection = (key, title, content) => (
    <Section
      key={key}
      title={title}
      section={key}
      previewHighlight={pickSectionHighlight(key, stats)}
      count={countFor[key]}
      isOpen={openSections.has(key)}
      onToggle={() => toggleSection(key)}
      accent={accentFor(key)}
    >
      {content}
    </Section>
  );

  return (
    <div style={{ margin: "0 auto", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* A1.5 §2.1 + §2.2 — Above-the-fold:
          AlmanacHero (4-stat marquee) → CareerTotalsCard (full-width
          sortable table) → FeaturedCardsRow (3 equal cards: Titles,
          Top Blowouts, Awards Spotlight). All-Time Wins card dropped —
          subset of Career Totals. */}
      <AlmanacHero pipelineState={pipelineState} />
      <CareerTotalsCard rows={stats.careerTotals} />
      <FeaturedCardsRow stats={stats} />

      {/* A1.5 §2.3 — Section order: Championships → Awards → Playoffs →
          Drafts → Trades → Single-Game → Streaks → Single-Season →
          Head-to-Head → Luck → Volatility → YoY Swings.
          Career Totals dropped (now the featured card above).
          Era Rankings remains dropped per AI_STRIPDOWN. */}
      {renderSection("championships", "Championships", renderChampionships(stats.championships))}

      {renderSection("awards", "Awards", renderAwards(stats))}

      {renderSection("playoffs", "Playoffs", renderPlayoffs(stats.playoffs))}

      {Array.isArray(stats.drafts) && stats.drafts.length > 0 &&
        renderSection("drafts", "Drafts", renderDrafts(stats.drafts, stats.meta?.earliestStatsYear))}

      {keepers.hasKeepers &&
        renderSection("keepers", "Keepers", renderKeepers(keepers))}

      {Array.isArray(stats.trades) && stats.trades.length > 0 &&
        renderSection("trades", "Trades", renderTrades(stats.trades, stats.tradeMeta))}

      {renderSection("singleGame", "Single-Game Records", renderSingleGame(stats.singleGame))}

      {renderSection("streaks", "Streaks", renderStreaks(stats.streaks))}

      {renderSection("singleSeason", "Single-Season Records", renderSingleSeason(stats.singleSeason, hasMedians, medianOpts))}

      {renderSection("pointsPerGame", "Points Per Game", renderPointsPerGame(stats))}

      {formatEvolution.hasContent &&
        renderSection("formatHistory", "League Format History", renderFormatHistory(formatEvolution))}

      {renderSection("headToHead", "Head-to-Head", renderHeadToHead(stats.headToHead))}

      {renderSection("luck", "Luck", renderLuck(stats.luck, hasMedians))}

      {renderSection("volatility", "Volatility", renderVolatility(stats.volatility))}

      {renderSection("yoySwings", "Year-over-Year Swings", renderYoySwings(stats.yoySwings, hasMedians, medianOpts))}

      {/* A1.5 §2.5 — ShareCardsGallery temporarily removed per user
          feedback. Component code + api/recap/share-card.js endpoint
          stay in the codebase for a v2 redesign. To restore, re-add:
            <ShareCardsGallery pipelineState={pipelineState} /> */}
    </div>
  );
}

// Helper — render W-L cell, with tooltip showing H2H + median breakdown when
// the league uses median wins. Accepts rows shaped either { totalW, totalL,
// w, l, medianW, medianL } (career/season) or { w, l } (H2H-only sections).
function renderWL(r, hasMedians) {
  if (hasMedians && r.medianW != null && (r.medianW > 0 || r.medianL > 0)) {
    const w = r.totalW ?? r.w;
    const l = r.totalL ?? r.l;
    return (
      <span title={`${r.w}-${r.l} H2H + ${r.medianW}-${r.medianL} median`} style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>
        {w}-{l}
      </span>
    );
  }
  const w = r.totalW ?? r.w;
  const l = r.totalL ?? r.l;
  return `${w}-${l}`;
}

function MedianNote({ show, firstYear, allYears }) {
  if (!show) return null;
  const text = !allYears && firstYear
    ? `W-L includes median wins for seasons from ${firstYear} onward (hover/tap for H2H breakdown).`
    : "W-L includes median wins (hover/tap for H2H breakdown).";
  return (
    <div style={{
      fontSize: 11, color: "#9ca3af", fontStyle: "italic",
      marginTop: 4, marginBottom: 8, fontFamily: "inherit",
    }}>
      {text}
    </div>
  );
}

// ============================================================================
// LEAGUE OVERVIEW — Spotify-Wrapped-style TL;DR card (UX_REFINEMENTS_v2 §2)
// ============================================================================
//
// Single big-card with 4 stat numbers + 3-4 notable-records lines.
// Renders between ShareCards and HeroBullets. Cheap to render —
// all data pre-computed in funStats.computeLeagueOverview.

function LeagueOverview({ overview, leagueName }) {
  if (!overview) return null;
  const stats = [
    overview.totalSeasons ? { value: overview.totalSeasons, label: "Seasons" } : null,
    overview.totalOwners ? { value: overview.totalOwners, label: "Owners" } : null,
    overview.totalChampionships ? { value: overview.totalChampionships, label: "Chips" } : null,
    overview.totalTrades ? { value: overview.totalTrades, label: "Trades" } : null,
    overview.rounds ? { value: overview.rounds, label: "Rounds" } : null,
  ].filter(Boolean);

  // Notable-records lines. Each renders only if data exists; the whole
  // block is hidden if all four are empty.
  const notables = [];
  if (overview.topPpg) {
    notables.push(`Highest-scoring season: ${overview.topPpg.owner} ${overview.topPpg.year} (${overview.topPpg.ppg.toFixed(1)} PPG)`);
  }
  if (overview.lowGame) {
    notables.push(`Lowest single-game: ${overview.lowGame.owner} ${overview.lowGame.year} Wk${overview.lowGame.week} (${overview.lowGame.pts.toFixed(1)} pts)`);
  }
  if (overview.longestDynasty) {
    const { owner, count, startYear, endYear } = overview.longestDynasty;
    notables.push(`Longest dynasty: ${owner} (${count} consecutive titles, ${startYear}-${endYear})`);
  }
  if (overview.formatString) {
    notables.push(`Format: ${overview.formatString}`);
  }

  return (
    <section
      id="recap-section-overview"
      className="recap-overview"
      style={{
        background: `linear-gradient(135deg, ${theme.colors.bg.raised} 0%, ${theme.colors.bg.surface} 100%)`,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 16,
        padding: "24px 20px",
      }}
    >
      <div className="recap-overview-stats" style={{
        display: "grid",
        gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
        gap: 12,
        marginBottom: notables.length ? 18 : 0,
      }}>
        {stats.map((s) => (
          <div key={s.label} style={{
            textAlign: "center",
            padding: "10px 6px",
          }}>
            <div style={{
              fontSize: 32, fontWeight: theme.fontWeight.black,
              color: theme.colors.text.primary, lineHeight: 1,
              fontFamily: theme.fonts.display,
              letterSpacing: "-1px",
            }}>{s.value}</div>
            <div style={{
              fontSize: 10, color: theme.colors.text.muted,
              letterSpacing: 2, textTransform: "uppercase",
              fontWeight: theme.fontWeight.heavy, marginTop: 4,
            }}>{s.label}</div>
          </div>
        ))}
      </div>
      {notables.length > 0 && (
        <div style={{
          display: "flex", flexDirection: "column", gap: 6,
          fontFamily: theme.fonts.body, fontSize: 13,
          color: theme.colors.text.primary, lineHeight: 1.55,
        }}>
          {notables.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================================================
// COLLAPSIBLE SECTION
// ============================================================================
//
// Default-collapsed wrapper around each stats section. Header shows title +
// count chip + deterministic one-line preview (src/engine/sectionHighlights).
// Click anywhere on the header → caret rotates 90°, body renders with all
// tables/grids inside. ID-anchored for potential deep linking later.

// VISUAL_OVERHAUL §"Section list redesign" — slim row with 4px accent
// border on the left, display-font title, right-aligned highlight when
// collapsed. Category accent color comes from CATEGORY_ACCENTS below;
// fallback is the brand accent.
function Section({ title, section, previewHighlight, count, isOpen, onToggle, children, accent }) {
  const ref = useSectionViewOnce(section);
  const borderColor = accent || theme.colors.brand.accent;
  return (
    <section
      id={`recap-section-${section}`}
      ref={ref}
      className={`recap-section recap-collapsible${isOpen ? " recap-section--open" : ""}`}
      style={{
        background: theme.colors.bg.raised,
        border: `1px solid ${theme.colors.border.default}`,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={`recap-section-body-${section}`}
        className="recap-section-row"
      >
        <span
          className="recap-section-caret"
          style={{ color: borderColor, transform: isOpen ? "rotate(90deg)" : "none" }}
        >▸</span>
        <h2 className="recap-section-title" style={{ fontFamily: theme.fonts.almanac }}>
          {title}
        </h2>
        {count && (
          <span className="recap-section-count">
            {count}
          </span>
        )}
        {!isOpen && previewHighlight && (
          <span className="recap-section-highlight">
            {previewHighlight}
          </span>
        )}
      </button>
      {isOpen && (
        <div id={`recap-section-body-${section}`} className="recap-section-body">
          {children}
        </div>
      )}
    </section>
  );
}

// VISUAL_OVERHAUL §"Category theming" — section key → accent token. Falls
// back to brand accent when a section isn't listed. Only used on the 4px
// left border + caret color per spec ("subtle accent, not chromatic
// chaos").
const CATEGORY_ACCENTS = {
  championships: "gold",
  drafts:        "blue",
  keepers:       "orange",
  trades:        "green",
  careerTotals:  "purple",
  singleGame:    "red",
  singleSeason:  "red",
  pointsPerGame: "purple",
  formatHistory: "blue",
  streaks:       "orange",
  headToHead:    "cyan",
  playoffs:      "gold",
  luck:          "yellow",
  volatility:    "pink",
  yoySwings:     "pink",
  awards:        "gold",
};

function accentFor(sectionKey) {
  const token = CATEGORY_ACCENTS[sectionKey];
  return token ? theme.colors.accents[token] : null;
}

// One-shot IntersectionObserver hook — fires recap_section_view exactly once
// per section per session when ≥50% of the section is in the viewport.
function useSectionViewOnce(sectionName) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !sectionName || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        captureSectionView(sectionName);
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [sectionName]);
  return ref;
}

// ============================================================================
// TABLES — responsive: desktop=table, mobile=stacked cards
// ============================================================================

function ResponsiveTable({ columns, rows, emptyMessage = "No data" }) {
  if (!rows?.length) return <Empty>{emptyMessage}</Empty>;
  return (
    <div className="recap-table-wrap">
      {/* Desktop table — hidden on mobile via CSS */}
      <table className="recap-table-desktop" style={{
        width: "100%", borderCollapse: "collapse",
        background: theme.colors.bg.raised,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 10, overflow: "hidden",
        fontFamily: theme.fonts.body, fontSize: 15,
        color: theme.colors.text.primary,
      }}>
        <thead>
          <tr style={{ background: theme.colors.bg.surface }}>
            {columns.map((c) => (
              <th key={c.key} style={{
                textAlign: c.align || "left", padding: "10px 12px",
                fontSize: 11, fontWeight: theme.fontWeight.heavy,
                color: theme.colors.text.muted, letterSpacing: 1.5,
                textTransform: "uppercase",
                borderBottom: `1px solid ${theme.colors.border.default}`,
              }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{
              borderTop: ri ? `1px solid ${theme.colors.border.subtle}` : "none",
            }}>
              {columns.map((c) => (
                <td key={c.key} style={{
                  padding: "9px 12px", textAlign: c.align || "left",
                  color: c.highlight ? theme.colors.brand.accent : undefined,
                  fontWeight: c.bold ? theme.fontWeight.heavy : undefined,
                }}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Mobile stacked cards — hidden on desktop via CSS.
          NOTE: do NOT set `display` inline here — inline styles beat
          stylesheets, so `display: flex` here would override the @media
          `display: none` rule and the cards would render on desktop too.
          Layout properties (flex-direction, gap) live in the CSS rule. */}
      <div className="recap-table-mobile">
        {rows.map((row, ri) => (
          <div key={ri} style={{
            background: theme.colors.bg.raised,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: 10, padding: "12px 14px",
            display: "flex", flexDirection: "column", gap: 4,
            fontFamily: theme.fonts.body, fontSize: 15,
            color: theme.colors.text.primary,
          }}>
            {columns.map((c) => (
              <div key={c.key} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{
                  color: theme.colors.text.muted, fontSize: 11,
                  fontWeight: theme.fontWeight.heavy, letterSpacing: 1,
                  textTransform: "uppercase",
                }}>{c.label}</span>
                <span style={{
                  textAlign: "right",
                  color: c.highlight ? theme.colors.brand.accent : undefined,
                  fontWeight: c.bold ? theme.fontWeight.heavy : undefined,
                }}>{c.render ? c.render(row) : row[c.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function SubHeading({ children }) {
  return (
    <div style={{
      fontSize: 12, letterSpacing: 2, color: theme.colors.text.muted,
      textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
      margin: "16px 0 8px",
    }}>{children}</div>
  );
}

function Empty({ children }) {
  return (
    <div style={{
      padding: 16, textAlign: "center",
      color: theme.colors.text.muted, fontFamily: theme.fonts.body, fontSize: 13,
      background: theme.colors.bg.raised, borderRadius: 10,
      border: `1px dashed ${theme.colors.border.default}`,
    }}>{children}</div>
  );
}

const fmt = (n, d = 1) => n == null ? "—" : Number(n).toFixed(d);
const pct = (n) => n == null ? "—" : `${(n * 100).toFixed(1)}%`;

// A2 Phase 1 — list of record-game rows, each click-to-expand to reveal
// the side-by-side lineups attached during pre-pull. The rows render the
// same content as ResponsiveTable but the row itself is a button + nested
// LineupView inside the open state. Indent 16px per spec.
function ExpandableRecordList({ rows, columns, getRowKey, getLineupProps, emptyMessage }) {
  const [openKey, setOpenKey] = useState(null);
  if (!rows?.length) {
    return <Empty>{emptyMessage || "No data"}</Empty>;
  }
  return (
    <div className="recap-record-list" style={{
      background: theme.colors.bg.raised,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 10,
      overflow: "hidden",
      fontFamily: theme.fonts.body, fontSize: 14,
    }}>
      {rows.map((row, idx) => {
        const key = getRowKey ? getRowKey(row, idx) : idx;
        const lp = getLineupProps ? getLineupProps(row) : null;
        const expandable = !!lp?.ownerLineup;
        const isOpen = openKey === key;
        return (
          <div key={key} style={{
            borderTop: idx ? `1px solid ${theme.colors.border.subtle}` : "none",
          }}>
            <button
              type="button"
              disabled={!expandable}
              onClick={() => expandable && setOpenKey(isOpen ? null : key)}
              aria-expanded={isOpen}
              style={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: `${columns.map(c => c.gridSize || "1fr").join(" ")} 24px`,
                gap: 12,
                alignItems: "center",
                padding: "10px 14px",
                background: "transparent",
                border: "none",
                textAlign: "left",
                color: theme.colors.text.primary,
                cursor: expandable ? "pointer" : "default",
                fontFamily: theme.fonts.body, fontSize: 14,
              }}
            >
              {columns.map((c) => (
                <span key={c.key} style={{
                  textAlign: c.align || "left",
                  color: c.highlight ? theme.colors.brand.accent : undefined,
                  fontWeight: c.bold ? theme.fontWeight.heavy : undefined,
                  whiteSpace: c.nowrap === false ? "normal" : "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                  minWidth: 0,
                }}>{c.render ? c.render(row) : row[c.key]}</span>
              ))}
              <span style={{
                color: expandable ? theme.colors.text.muted : "rgba(148,163,184,0.25)",
                fontWeight: 700, fontSize: 12, textAlign: "right",
              }} aria-hidden="true">
                {expandable ? (isOpen ? "▾" : "▸") : ""}
              </span>
            </button>
            {isOpen && expandable && (
              <div style={{
                padding: "0 14px 14px 30px",
                background: "rgba(255,255,255,0.02)",
              }}>
                <LineupView
                  ownerName={lp.ownerName}
                  oppName={lp.oppName}
                  ownerLineup={lp.ownerLineup}
                  oppLineup={lp.oppLineup}
                  year={lp.year}
                  week={lp.week}
                  source={lp.source}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// PER-SECTION RENDERERS
// ============================================================================

function renderChampionships(c) {
  if (!c) return null;
  const yearRows = (c.years || []).slice().reverse();
  const titleRows = Object.entries(c.titleCounts || {})
    .map(([owner, years]) => ({ owner, count: years.length, years: years.join(", ") }))
    .sort((a, b) => b.count - a.count);
  return (
    <>
      <SubHeading>Title Leaders</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "count", label: "Titles", highlight: true, align: "right", render: (r) => `${r.count}` },
          { key: "years", label: "Years", render: (r) => r.years },
        ]}
        rows={titleRows}
        emptyMessage="No championship data."
      />
      <SubHeading>Year-by-Year</SubHeading>
      {/* A2 Phase 1 — click a year to see the championship game's lineups
          (pre-pulled per spec scope). Rows without a pre-pulled lineup
          are static (no chevron). */}
      <ExpandableRecordList
        rows={yearRows}
        getRowKey={(r) => r.year}
        columns={[
          { key: "year", gridSize: "70px", bold: true, render: (r) => r.year },
          { key: "champion", gridSize: "1.4fr", highlight: true, render: (r) => r.champion || "—" },
          { key: "runnerUp", gridSize: "1.4fr", render: (r) => r.runnerUp || "—" },
          { key: "third", gridSize: "1.2fr", render: (r) => r.third || "—" },
          { key: "mostPoints", gridSize: "1.2fr", render: (r) => r.mostPoints || "—" },
        ]}
        getLineupProps={(r) => r.lineup ? {
          ownerName: r.champion, oppName: r.runnerUp,
          ownerLineup: r.lineup, oppLineup: r.opponentLineup,
          year: r.year, week: r.week, source: "prepull",
        } : null}
      />
    </>
  );
}

function renderCareerTotals(rows, hasMedians, opts) {
  if (!rows?.length) return null;
  return (
    <>
      <MedianNote {...(opts || { show: hasMedians })} />
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "wl", label: "W-L", align: "right", render: (r) => renderWL(r, hasMedians) },
          { key: "winPct", label: "Win %", align: "right", highlight: true, render: (r) => pct(r.totalWinPct ?? r.winPct) },
          { key: "ppg", label: "PPG", align: "right", render: (r) => fmt(r.ppg) },
          { key: "pf", label: "PF", align: "right", render: (r) => fmt(r.pf, 0) },
        ]}
        rows={rows}
      />
    </>
  );
}

function renderSingleGame(sg) {
  if (!sg) return null;
  return (
    <>
      <SubHeading>Highest Scores</SubHeading>
      {/* A2 Phase 1 — top-10 highest games are pre-pulled with lineups.
          Click a row to expand the side-by-side lineup view. */}
      <ExpandableRecordList
        rows={(sg.highest || []).slice(0, 10)}
        getRowKey={(r, i) => `${r.year}-${r.week}-${r.owner}-${i}`}
        columns={[
          { key: "year", gridSize: "90px", render: (r) => `${r.year} W${r.week}` },
          { key: "owner", gridSize: "1.2fr", bold: true, render: (r) => r.owner },
          { key: "matchup", gridSize: "1.6fr", align: "right", render: (r) => (
            <>
              <span style={{ color: "#fb923c", fontWeight: 700 }}>{fmt(r.pts)}</span>
              <span style={{ opacity: 0.7 }}> vs {fmt(r.oppPts)} {r.opp}</span>
            </>
          )},
        ]}
        getLineupProps={(r) => r.lineup ? {
          ownerName: r.owner, oppName: r.opp,
          ownerLineup: r.lineup, oppLineup: r.opponentLineup,
          year: r.year, week: r.week, source: "prepull",
        } : null}
      />
      <SubHeading>Biggest Blowouts</SubHeading>
      {/* A2 Phase 1 — top-5 blowouts are pre-pulled; rows 6-10 stay static. */}
      <ExpandableRecordList
        rows={(sg.biggestBlowouts || []).slice(0, 10)}
        getRowKey={(r, i) => `${r.year}-${r.week}-${r.owner}-${i}`}
        columns={[
          { key: "year", gridSize: "90px", render: (r) => `${r.year} W${r.week}` },
          { key: "owner", gridSize: "1.2fr", bold: true, render: (r) => r.owner },
          { key: "margin", gridSize: "70px", highlight: true, align: "right", render: (r) => `+${fmt(r.margin)}` },
          { key: "matchup", gridSize: "1.6fr", render: (r) => `${fmt(r.pts)} vs ${fmt(r.oppPts)} ${r.opp}` },
        ]}
        getLineupProps={(r) => r.lineup ? {
          ownerName: r.owner, oppName: r.opp,
          ownerLineup: r.lineup, oppLineup: r.opponentLineup,
          year: r.year, week: r.week, source: "prepull",
        } : null}
      />
      <SubHeading>Closest Games</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "year", label: "Year/Wk", render: (r) => `${r.year} W${r.week}` },
          { key: "owner", label: "Winner", bold: true },
          { key: "margin", label: "Margin", highlight: true, align: "right", render: (r) => fmt(r.margin) },
          { key: "matchup", label: "Score", render: (r) => `${fmt(r.pts)} vs ${fmt(r.oppPts)} ${r.opp}` },
        ]}
        rows={(sg.closestGames || []).slice(0, 10)}
      />
    </>
  );
}

function renderStreaks(s) {
  if (!s) return null;
  return (
    <>
      <SubHeading>Longest Win Streaks</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "length", label: "Games", highlight: true, align: "right" },
          { key: "startedAt", label: "Started", render: (r) => r.startedAt || "—" },
        ]}
        rows={(s.longestWinStreaks || []).filter((x) => x.length > 0).slice(0, 10)}
      />
      <SubHeading>Longest Losing Streaks</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "length", label: "Games", highlight: true, align: "right" },
          { key: "startedAt", label: "Started", render: (r) => r.startedAt || "—" },
        ]}
        rows={(s.longestLosingStreaks || []).filter((x) => x.length > 0).slice(0, 10)}
      />
    </>
  );
}

function renderSingleSeason(s, hasMedians, opts) {
  if (!s) return null;
  return (
    <>
      <SubHeading>Best Regular-Season Records</SubHeading>
      <MedianNote {...(opts || { show: hasMedians })} />
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "wl", label: "W-L", highlight: true, align: "right", render: (r) => renderWL(r, hasMedians) },
          { key: "ppg", label: "PPG", align: "right", render: (r) => fmt(r.ppg) },
        ]}
        rows={s.bestRecords || []}
      />
      <SubHeading>Worst Regular-Season Records</SubHeading>
      <MedianNote {...(opts || { show: hasMedians })} />
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "wl", label: "W-L", highlight: true, align: "right", render: (r) => renderWL(r, hasMedians) },
          { key: "ppg", label: "PPG", align: "right", render: (r) => fmt(r.ppg) },
        ]}
        rows={s.worstRecords || []}
      />
      <SubHeading>Highest PPG Seasons</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "ppg", label: "PPG", highlight: true, align: "right", render: (r) => fmt(r.ppg) },
          { key: "wl", label: "W-L", align: "right", render: (r) => renderWL(r, hasMedians) },
        ]}
        rows={s.highestPPG || []}
      />
    </>
  );
}

// A2 R3 #14 — dedicated Points Per Game section. Pure render against
// existing fields: careerTotals[].ppg/.pf/.games (funStats.js:71-77) and
// seasonRecords[].ppg (funStats.js:178). Top half is a career PPG
// leaderboard with the same current/former split as CareerTotalsCard
// (uses the .core flag stamped during funStats); bottom half is best
// single-season PPG straight from stats.singleSeason.highestPPG.
function renderPointsPerGame(stats) {
  if (!stats?.careerTotals?.length) return null;
  // Sort copy — careerTotals upstream is sorted by totalW, we want PPG.
  const ppgSorted = [...stats.careerTotals]
    .filter(r => (r.games || 0) > 0)
    .sort((a, b) => (b.ppg || 0) - (a.ppg || 0));
  const currentRows = ppgSorted.filter(r => r.core !== false);
  const formerRows = ppgSorted.filter(r => r.core === false);
  const careerCols = [
    { key: "owner", label: "Owner", bold: true },
    { key: "games", label: "Games", align: "right", render: (r) => r.games || 0 },
    { key: "pf", label: "PF", align: "right", render: (r) => Math.round(r.pf || 0) },
    { key: "ppg", label: "PPG", highlight: true, align: "right", render: (r) => fmt(r.ppg) },
  ];
  return (
    <>
      <SubHeading>Career PPG Leaderboard</SubHeading>
      <ResponsiveTable columns={careerCols} rows={currentRows} />
      {formerRows.length > 0 && (
        <>
          {/* Mirror CareerTotalsCard's "Former Owners" divider treatment. */}
          <div style={{
            marginTop: 10,
            fontFamily: theme.fonts.almanac,
            fontSize: 11, letterSpacing: 1.8,
            textTransform: "uppercase",
            color: theme.colors.text.muted,
            paddingTop: 8,
            borderTop: `1px solid ${theme.colors.border?.subtle || "rgba(255,255,255,0.08)"}`,
          }}>Former Owners</div>
          <div style={{ opacity: 0.62 }}>
            <ResponsiveTable columns={careerCols} rows={formerRows} />
          </div>
        </>
      )}
      <SubHeading>Best Single-Season PPG</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "games", label: "Games", align: "right" },
          { key: "ppg", label: "PPG", highlight: true, align: "right", render: (r) => fmt(r.ppg) },
        ]}
        rows={(stats.singleSeason?.highestPPG || []).slice(0, 10)}
      />
    </>
  );
}

// #22-keeper — team-centric Keepers section: each owner's kept players across
// all seasons, sorted by who's kept the most. `keepers` shape comes from
// collectKeeperHistory. Renders for both platforms (Sleeper dynasty keepers +
// Yahoo redraft keepers detected from roster is_keeper.kept).
function renderKeepers(keepers) {
  if (!keepers?.owners?.length) return null;
  return (
    <>
      <SubHeading>Keepers by Team — all seasons</SubHeading>
      <div style={{
        display: "flex", flexDirection: "column", gap: 0,
        background: theme.colors.bg.raised,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 10, overflow: "hidden",
      }}>
        {keepers.owners.map((o, idx) => (
          <div key={o.owner} style={{
            display: "grid",
            gridTemplateColumns: "140px 1fr",
            gap: 12, alignItems: "baseline",
            padding: "10px 14px",
            borderTop: idx ? `1px solid ${theme.colors.border.subtle}` : "none",
            fontFamily: theme.fonts.body, fontSize: 13,
          }}>
            <div>
              <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>{o.owner}</div>
              <div style={{
                fontSize: 10, letterSpacing: 1.2, textTransform: "uppercase",
                fontWeight: 700, color: theme.colors.text.muted, marginTop: 2,
              }}>{o.count} kept</div>
            </div>
            <ul style={{
              listStyle: "none", margin: 0, padding: 0,
              display: "flex", flexDirection: "column", gap: 3,
            }}>
              {o.keepers.map((k, i) => (
                <li key={i} style={{ color: theme.colors.text.muted }}>
                  <span style={{
                    fontFamily: theme.fonts.almanac, fontWeight: 700,
                    color: theme.colors.brand.accent, marginRight: 8,
                  }}>{k.year}</span>
                  <span style={{ color: theme.colors.text.primary, fontWeight: 600 }}>{k.player}</span>
                  {(k.position || k.team) && (
                    <span style={{ marginLeft: 6 }}>
                      ({k.position || ""}{k.position && k.team ? " " : ""}{k.team || ""})
                    </span>
                  )}
                  {k.round != null && (
                    <span style={{ marginLeft: 6, opacity: 0.7 }}>· R{k.round}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </>
  );
}

// A2 R3 #15 + #16 — League Format History timeline. Two stacked blocks:
//   1. Format changes year-by-year (roster slots, scoring, median, playoffs)
//   2. Per-season label (Startup / Rookie Draft / Keeper / Redraft) + the
//      kept-player list (both platforms now expose is_keeper picks).
// `evolution` shape is produced by computeLeagueEvolution.
function renderFormatHistory(evolution) {
  if (!evolution || !evolution.hasContent) return null;
  return (
    <>
      <SubHeading>Format Changes</SubHeading>
      <div style={{
        display: "flex", flexDirection: "column", gap: 10,
        background: theme.colors.bg.raised,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 10, padding: "12px 14px",
      }}>
        {evolution.formatChanges.map((entry) => (
          <div key={entry.year} style={{
            display: "grid",
            gridTemplateColumns: "72px 1fr",
            gap: 12, alignItems: "baseline",
          }}>
            <div style={{
              fontFamily: theme.fonts.almanac,
              fontSize: 14, fontWeight: 700,
              color: entry.isBaseline ? theme.colors.text.muted : theme.colors.brand.accent,
              letterSpacing: 0.5,
            }}>
              {entry.year}
              {entry.isBaseline && (
                <div style={{
                  fontSize: 9, letterSpacing: 1.5,
                  textTransform: "uppercase", marginTop: 2,
                  color: theme.colors.text.muted,
                }}>baseline</div>
              )}
            </div>
            <ul style={{
              listStyle: "none", margin: 0, padding: 0,
              display: "flex", flexDirection: "column", gap: 4,
              fontFamily: theme.fonts.body, fontSize: 13,
              color: theme.colors.text.primary,
            }}>
              {entry.changes.map((line, i) => (
                <li key={i} style={{ lineHeight: 1.45 }}>{line}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <SubHeading>Per-Season Draft &amp; Keepers</SubHeading>
      <div style={{
        display: "flex", flexDirection: "column", gap: 0,
        background: theme.colors.bg.raised,
        border: `1px solid ${theme.colors.border.default}`,
        borderRadius: 10, overflow: "hidden",
      }}>
        {evolution.seasonLabels.map((sl, idx) => (
          <div key={sl.year} style={{
            display: "grid",
            gridTemplateColumns: "72px 1fr",
            gap: 12, alignItems: "baseline",
            padding: "10px 14px",
            borderTop: idx ? `1px solid ${theme.colors.border.subtle}` : "none",
            fontFamily: theme.fonts.body, fontSize: 13,
          }}>
            <div style={{
              fontFamily: theme.fonts.almanac,
              fontSize: 14, fontWeight: 700,
              color: theme.colors.text.primary,
            }}>{sl.year}</div>
            <div>
              <div>
                <span style={{ fontWeight: 700, color: theme.colors.brand.accent }}>{sl.label}</span>
                {sl.draftLabel && (
                  <span style={{ color: theme.colors.text.muted, marginLeft: 8 }}>
                    · {sl.draftLabel} draft
                  </span>
                )}
              </div>
              {sl.keptPlayers && sl.keptPlayers.length > 0 && (
                <div style={{
                  marginTop: 6, fontSize: 12,
                  color: theme.colors.text.muted,
                }}>
                  <div style={{
                    fontSize: 10, letterSpacing: 1.2,
                    textTransform: "uppercase", fontWeight: 700,
                    marginBottom: 4,
                  }}>{sl.keptPlayers.length} kept</div>
                  <ul style={{
                    listStyle: "none", margin: 0, padding: 0,
                    display: "flex", flexDirection: "column", gap: 2,
                  }}>
                    {sl.keptPlayers.map((k, i) => (
                      <li key={i}>
                        <span style={{ color: theme.colors.text.primary, fontWeight: 600 }}>{k.owner || "—"}</span>
                        <span style={{ marginLeft: 8 }}>kept</span>
                        <span style={{ marginLeft: 6, color: theme.colors.text.primary }}>{k.player}</span>
                        {(k.position || k.team) && (
                          <span style={{ marginLeft: 6 }}>
                            ({k.position || ""}{k.position && k.team ? " " : ""}{k.team || ""})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function renderHeadToHead(h) {
  if (!h) return null;
  const rivalries = (h.lopsidedRivalries || []).map((r) => ({ ...r }));
  return (
    <>
      <SubHeading>Most Lopsided Rivalries (10+ meetings, 60%+)</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "winner", label: "Owner", bold: true },
          { key: "loser", label: "Over" },
          { key: "wl", label: "Record", highlight: true, align: "right", render: (r) => `${r.w}-${r.l}` },
          { key: "winPct", label: "Win %", align: "right", render: (r) => pct(r.winPct) },
          { key: "meetings", label: "Games", align: "right" },
        ]}
        rows={rivalries.slice(0, 15)}
        emptyMessage="No lopsided rivalries (need 10+ meetings)."
      />
    </>
  );
}

function renderEraRankings(eras, hasMedians, opts) {
  if (!eras) return null;
  return (
    <>
      {Object.entries(eras).map(([eraName, rows]) => (
        <div key={eraName}>
          <SubHeading>{eraName}</SubHeading>
          <MedianNote {...(opts || { show: hasMedians })} />
          <ResponsiveTable
            columns={[
              { key: "owner", label: "Owner", bold: true },
              { key: "wl", label: "W-L", align: "right", render: (r) => renderWL(r, hasMedians) },
              { key: "winPct", label: "Win %", highlight: true, align: "right", render: (r) => pct(r.winPct) },
              { key: "ppg", label: "PPG", align: "right", render: (r) => fmt(r.ppg) },
            ]}
            rows={rows}
          />
        </div>
      ))}
    </>
  );
}

function renderPlayoffs(p) {
  if (!p) return null;
  return (
    <>
      <SubHeading>Career Playoff Records</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "wl", label: "W-L", align: "right", render: (r) => `${r.w}-${r.l}` },
          { key: "winPct", label: "Win %", highlight: true, align: "right", render: (r) => pct(r.winPct) },
          { key: "ppg", label: "PPG", align: "right", render: (r) => fmt(r.ppg) },
          { key: "games", label: "Games", align: "right" },
        ]}
        rows={p.careerRecords || []}
        emptyMessage="No playoff data."
      />
      <SubHeading>Highest Single-Postseason PPG</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "ppg", label: "PPG", highlight: true, align: "right", render: (r) => fmt(r.ppg) },
          { key: "wl", label: "Record", align: "right", render: (r) => `${r.w}-${r.l}` },
        ]}
        rows={p.highestPPG || []}
        emptyMessage="No multi-game playoff runs."
      />
      {p.closestGames?.length > 0 && (
        <>
          {/* A1.6 §5 — closest playoff matchups. Sourced from funStats's
              playoffs array (m.playoff && !m.consolation), separate from
              the regular-season-only singleGame.closestGames. */}
          <SubHeading>Closest Playoff Matchups</SubHeading>
          <ResponsiveTable
            columns={[
              { key: "year", label: "Year/Wk", render: (r) => `${r.year} W${r.week}` },
              // A1.6 R2 #13 — round/place label. Sleeper sources the
              // exact placement from winnersBracket (p:1 → Championship,
              // p:3 → 3rd Place, etc.); Yahoo derives positionally from
              // the season's non-consolation playoff week range.
              { key: "round", label: "Round", render: (r) => r.playoffLabel || "—" },
              { key: "owner", label: "Winner", bold: true },
              { key: "margin", label: "Margin", highlight: true, align: "right", render: (r) => `+${fmt(r.margin)}` },
              { key: "matchup", label: "Score", render: (r) => `${fmt(r.pts)} vs ${fmt(r.oppPts)} ${r.opp}` },
            ]}
            rows={p.closestGames}
          />
        </>
      )}
    </>
  );
}

function renderLuck(l, hasMedians) {
  if (!l) return null;
  // In median leagues, Career Totals + single-season tables include median
  // wins in their W/L. Luck's Record column is intentionally H2H-only
  // (luck is computed against actual head-to-head outcomes, not the
  // median-bonus column). Qualify the label so readers don't mistake
  // these for the median-inclusive totals shown elsewhere.
  const recordLabel = hasMedians ? "Record (H2H)" : "Record";
  return (
    <>
      <SubHeading>All-Play Records (Skill vs Luck)</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "apPct", label: "All-Play %", align: "right", render: (r) => pct(r.apPct) },
          { key: "actPct", label: "Actual %", align: "right", render: (r) => pct(r.actPct) },
          { key: "luck", label: "Luck", highlight: true, align: "right", render: (r) => `${r.luck > 0 ? "+" : ""}${pct(r.luck)}` },
        ]}
        rows={l.allPlay || []}
      />
      <SubHeading>Luckiest Seasons</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "wl", label: recordLabel, align: "right", render: (r) => `${r.w}-${r.l}` },
          { key: "luck", label: "Luck", highlight: true, align: "right", render: (r) => `+${pct(r.luck)}` },
        ]}
        rows={l.luckiestSeasons || []}
      />
      <SubHeading>Unluckiest Seasons</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "year", label: "Year" },
          { key: "wl", label: recordLabel, align: "right", render: (r) => `${r.w}-${r.l}` },
          { key: "luck", label: "Luck", highlight: true, align: "right", render: (r) => pct(r.luck) },
        ]}
        rows={l.unluckiestSeasons || []}
      />
    </>
  );
}

function renderVolatility(v) {
  if (!v) return null;
  return (
    <>
      <SubHeading>Weekly High / Bottom Score Rates</SubHeading>
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "top", label: "Top", align: "right" },
          { key: "bottom", label: "Bottom", align: "right" },
          { key: "topPct", label: "Top %", highlight: true, align: "right", render: (r) => pct(r.topPct) },
          { key: "bottomPct", label: "Bot %", align: "right", render: (r) => pct(r.bottomPct) },
        ]}
        rows={v.weeklyTopBottom || []}
      />
      {/* Patch 2 — Boom/Bust Index removed from display (over-clever for
          Almanac framing). compute still runs in funStats.volatility.boomBust
          so a v2 resurrection would just re-add the table. */}
    </>
  );
}

function renderYoySwings(y, hasMedians, opts) {
  if (!y) return null;
  return (
    <>
      <SubHeading>Biggest Comebacks</SubHeading>
      <MedianNote {...(opts || { show: hasMedians })} />
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "prev", label: "Prev Year", render: (r) => `${r.prevYear} (${r.prevW}-${r.prevL})` },
          { key: "year", label: "This Year", render: (r) => `${r.year} (${r.w}-${r.l})` },
          { key: "swing", label: "Swing", highlight: true, align: "right", render: (r) => `+${pct(r.swing)}` },
        ]}
        rows={y.biggestComebacks || []}
      />
      <SubHeading>Biggest Falls</SubHeading>
      <MedianNote {...(opts || { show: hasMedians })} />
      <ResponsiveTable
        columns={[
          { key: "owner", label: "Owner", bold: true },
          { key: "prev", label: "Prev Year", render: (r) => `${r.prevYear} (${r.prevW}-${r.prevL})` },
          { key: "year", label: "This Year", render: (r) => `${r.year} (${r.w}-${r.l})` },
          { key: "swing", label: "Swing", highlight: true, align: "right", render: (r) => pct(r.swing) },
        ]}
        rows={y.biggestFalls || []}
      />
    </>
  );
}

// A1.5 §2.4 — 10 universal + 2 dynasty-only award cards, each expandable
// to show contributing events. Reads from stats.almanacAwards
// (pre-resolved by funStats.computeAlmanacAwards) and falls through to
// stats.trades for Wheeler & Dealer's per-trade detail.
const AWARDS_UNIVERSAL = [
  "bridesmaid",
  "topDog",
  "heartbreaker",
  "escapeArtist",
  "hotHand",
  "coldSnap",
  "playoffMainstay",
  "ownsYoSoul",
  "wheelerDealer",
  "titleDrought",
];
const AWARDS_DYNASTY = ["biggestCollapse", "comebackKid"];

function renderAwards(stats) {
  const awards = stats?.almanacAwards;
  if (!awards) return null;
  const isDynasty = !!stats?.meta?.isDynasty;
  const slugs = isDynasty
    ? [...AWARDS_UNIVERSAL, ...AWARDS_DYNASTY]
    : AWARDS_UNIVERSAL;
  const present = slugs.map((s) => awards[s]).filter(Boolean);
  if (!present.length) return null;
  return (
    <div className="almanac-awards-grid">
      {present.map((a) => (
        <AlmanacAwardRow key={a.slug} award={a} stats={stats} />
      ))}
    </div>
  );
}

function AlmanacAwardRow({ award, stats }) {
  const [open, setOpen] = useState(false);
  const hasDetails = awardHasDetails(award, stats);
  const onToggle = () => { if (hasDetails) setOpen((v) => !v); };
  return (
    <div className={`almanac-awards-card${open ? " almanac-awards-card--open" : ""}${award.empty ? " almanac-awards-card--empty" : ""}`}>
      <button
        type="button"
        className="almanac-awards-head"
        onClick={onToggle}
        aria-expanded={open}
        disabled={!hasDetails}
      >
        <span className="almanac-awards-icon">{award.icon}</span>
        <div className="almanac-awards-text">
          <div className="almanac-awards-label">{award.label}</div>
          {award.owner ? (
            <>
              <div className="almanac-awards-owner">{award.owner}</div>
              <div className="almanac-awards-context">{award.context}</div>
            </>
          ) : (
            <div className="almanac-awards-context" style={{ fontStyle: "italic" }}>
              {award.context}
            </div>
          )}
        </div>
        {hasDetails && (
          <span className="almanac-awards-caret" aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
        )}
      </button>
      {open && hasDetails && (
        <div className="almanac-awards-detail">
          <AwardDetailContent award={award} stats={stats} />
        </div>
      )}
    </div>
  );
}

// Per-award detail renderer. Each award's `details` shape is custom (see
// funStats.computeAlmanacAwards for what gets attached). Wheeler & Dealer
// is special — its detail comes from filtering stats.trades at render
// time rather than from award.details.
function AwardDetailContent({ award, stats }) {
  switch (award.slug) {
    case "bridesmaid":
      return (
        <ul className="almanac-detail-list">
          {(award.details || []).map((d, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{d.year}</span>
              <span className="almanac-detail-val">runner-up to {d.champion}</span>
            </li>
          ))}
        </ul>
      );
    case "topDog":
      return (
        <ul className="almanac-detail-list">
          {(award.details || []).map((d, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{d.year}</span>
              <span className="almanac-detail-val">{d.ppg} PPG (#{d.rank})</span>
            </li>
          ))}
        </ul>
      );
    case "heartbreaker":
    case "escapeArtist": {
      const games = award.details || [];
      const verb = award.slug === "heartbreaker" ? "lost to" : "beat";
      return (
        <ul className="almanac-detail-list">
          {games.map((g, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{g.year} W{g.week}</span>
              <span className="almanac-detail-val">
                {verb} {g.opp} {g.pts}–{g.oppPts}
                <span className="almanac-detail-meta"> · margin {g.margin}</span>
              </span>
            </li>
          ))}
        </ul>
      );
    }
    case "hotHand":
    case "coldSnap": {
      const games = award.details || [];
      const verb = award.slug === "hotHand" ? "beat" : "lost to";
      return (
        <ul className="almanac-detail-list">
          {games.map((g, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{g.year} W{g.week}</span>
              <span className="almanac-detail-val">
                {verb} {g.opp} {g.pts}–{g.oppPts}
                {g.playoff && <span className="almanac-detail-meta"> · playoff</span>}
              </span>
            </li>
          ))}
        </ul>
      );
    }
    case "playoffMainstay":
      return (
        <ul className="almanac-detail-list almanac-detail-list--compact">
          {(award.details || []).map((d, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{d.year}</span>
              <span className="almanac-detail-val">made playoffs</span>
            </li>
          ))}
        </ul>
      );
    case "ownsYoSoul": {
      const games = award.details?.games || [];
      const opp = award.details?.opponent || "";
      return (
        <ul className="almanac-detail-list">
          {games.map((g, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{g.year} W{g.week}</span>
              <span className="almanac-detail-val">
                {g.won ? "won" : "lost"} {g.ownerPts}–{g.oppPts} vs {opp}
                {g.playoff && <span className="almanac-detail-meta"> · playoff</span>}
              </span>
            </li>
          ))}
        </ul>
      );
    }
    case "wheelerDealer": {
      // Render-time filter — trades have full per-participant shape.
      const trades = (stats?.trades || []).filter((t) =>
        t.participants?.some((p) => p.owner === award.owner)
      );
      return (
        <ul className="almanac-detail-list">
          {trades.map((t) => {
            const me = t.participants.find((p) => p.owner === award.owner);
            const others = t.participants.filter((p) => p.owner !== award.owner).map((p) => p.owner);
            const received = (me?.assetsReceived || []).length;
            const sent = (me?.assetsSent || []).length;
            return (
              <li key={t.tradeId}>
                <span className="almanac-detail-key">{t.season}{t.week ? ` W${t.week}` : ""}</span>
                <span className="almanac-detail-val">
                  with {others.join(", ") || "—"}
                  <span className="almanac-detail-meta"> · received {received}, sent {sent}</span>
                </span>
              </li>
            );
          })}
        </ul>
      );
    }
    case "titleDrought":
      return (
        <ul className="almanac-detail-list almanac-detail-list--compact">
          {(award.details || []).map((d, i) => (
            <li key={i}>
              <span className="almanac-detail-key">{d.year}</span>
              <span className="almanac-detail-val">no title</span>
            </li>
          ))}
        </ul>
      );
    case "biggestCollapse":
    case "comebackKid": {
      const d = award.details;
      if (!d) return null;
      return (
        <ul className="almanac-detail-list">
          <li>
            <span className="almanac-detail-key">{d.prevYear}</span>
            <span className="almanac-detail-val">{d.prevW}-{d.prevL}</span>
          </li>
          <li>
            <span className="almanac-detail-key">{d.year}</span>
            <span className="almanac-detail-val">{d.w}-{d.l}</span>
          </li>
          <li>
            <span className="almanac-detail-key">Swing</span>
            <span className="almanac-detail-val">
              {d.swing > 0 ? "+" : ""}{d.swing}%
            </span>
          </li>
        </ul>
      );
    }
    default:
      return null;
  }
}

function awardHasDetails(award, stats) {
  if (award.empty) return false;
  if (award.slug === "wheelerDealer") {
    return (stats?.trades || []).some((t) =>
      t.participants?.some((p) => p.owner === award.owner)
    );
  }
  if (Array.isArray(award.details)) return award.details.length > 0;
  if (award.details && typeof award.details === "object") {
    // Owns Yo' Soul shape: { opponent, games[] }
    if (Array.isArray(award.details.games)) return award.details.games.length > 0;
    return true; // Collapse/Comeback object
  }
  return false;
}

// UX_REFINEMENTS_v2 §4 — Drafts section restructure.
// Dynasty leagues now group:
//   1. STARTUP — the foundational 200-300 pick draft, surfaced with the
//      "FOUNDING DRAFT" badge and bigger visual weight
//   2. ROOKIE DRAFTS — annual rookie drafts grouped under one sub-header,
//      each year individually collapsible
// Redraft leagues (no startup/rookie split) render under a single
// "Annual Drafts" sub-header, default-collapsed per year.
function renderDrafts(drafts, earliestStatsYear) {
  if (!drafts?.length) return null;
  const startup = drafts.find(d => d.draftEventType === 'startup') || null;
  const rookies = drafts.filter(d => d.draftEventType === 'rookie');
  const annuals = drafts.filter(d => d.draftEventType === 'annual');
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {startup && (
        <div>
          <DraftSubHeading>Startup Draft</DraftSubHeading>
          <DraftYearCard draft={startup} earliestStatsYear={earliestStatsYear} isFounding />
        </div>
      )}
      {rookies.length > 0 && (
        <div>
          <DraftSubHeading>Rookie Drafts</DraftSubHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rookies.map(d => (
              <DraftYearCard
                key={d.draft_id || (d.year + "-" + d.league_id)}
                draft={d}
                earliestStatsYear={earliestStatsYear}
              />
            ))}
          </div>
        </div>
      )}
      {annuals.length > 0 && (
        <div>
          {/* For redraft leagues we keep the sub-header lightweight — no
              startup/rookie distinction means the group is just "all
              annual drafts" and the section title above already says
              "Drafts". Skip the sub-header when annuals are the ONLY
              group present (most common redraft case). */}
          {(startup || rookies.length > 0) && (
            <DraftSubHeading>Annual Drafts</DraftSubHeading>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {annuals.map(d => (
              <DraftYearCard
                key={d.draft_id || (d.year + "-" + d.league_id)}
                draft={d}
                earliestStatsYear={earliestStatsYear}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TRADES (task #38) — per-trade cards with both sides of each deal
// ============================================================================
//
// Trades section between Drafts and Career Totals. Each trade renders as a
// single card showing the participants and what each one received/sent.
// Sleeper trades include picks (with "(originally X's)" context) and FAAB.
// Yahoo trades are player-only.

function renderTrades(trades, tradeMeta) {
  if (!trades?.length) return null;
  // Group by season for visual scannability — a 200-trade dynasty league
  // would be a wall of cards otherwise. Each season is collapsible with
  // newest-first order overall.
  const bySeason = {};
  for (const t of trades) {
    if (!bySeason[t.season]) bySeason[t.season] = [];
    bySeason[t.season].push(t);
  }
  const years = Object.keys(bySeason).sort((a, b) => Number(b) - Number(a));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {tradeMeta?.totalTrades > 0 && tradeMeta.topPair && (
        <div style={{
          fontSize: 12, color: theme.colors.text.muted, fontFamily: theme.fonts.body,
          fontStyle: "italic", marginBottom: -6,
        }}>
          Most active pair: {tradeMeta.topPair.ownerA} & {tradeMeta.topPair.ownerB}
          {" "}({tradeMeta.topPair.count} deal{tradeMeta.topPair.count === 1 ? "" : "s"}).
        </div>
      )}
      {years.map(year => (
        <div key={year}>
          <DraftSubHeading>{year}</DraftSubHeading>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {bySeason[year].map(t => <TradeCard key={t.tradeId} trade={t} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function TradeCard({ trade }) {
  const participants = trade.participants || [];
  const tsLabel = trade.timestamp
    ? new Date(trade.timestamp).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      })
    : null;
  return (
    <div style={{
      background: theme.colors.bg.raised,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 10,
      padding: "12px 14px",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        fontSize: 10, letterSpacing: 1.5, textTransform: "uppercase",
        color: theme.colors.text.muted, fontWeight: theme.fontWeight.heavy,
        marginBottom: 10, fontFamily: theme.fonts.display,
      }}>
        <span>{participants.length}-team trade</span>
        <span>{tsLabel || `Week ${trade.week ?? "—"}`}</span>
      </div>
      <div className="trade-grid" style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.min(participants.length, 2)}, 1fr)`,
        gap: 12,
      }}>
        {participants.map((p, i) => (
          <TradeParticipant key={`${trade.tradeId}-${i}`} participant={p} />
        ))}
      </div>
    </div>
  );
}

function TradeParticipant({ participant }) {
  return (
    <div style={{
      background: theme.colors.bg.surface,
      border: `1px solid ${theme.colors.border.subtle}`,
      borderRadius: 8,
      padding: "10px 12px",
    }}>
      <div style={{
        fontSize: 13, fontWeight: theme.fontWeight.heavy,
        color: theme.colors.text.primary, marginBottom: 8,
        fontFamily: theme.fonts.display,
      }}>{participant.owner}</div>
      <AssetList label="Received" assets={participant.assetsReceived} variant="received" />
      <AssetList label="Sent"     assets={participant.assetsSent}     variant="sent" />
    </div>
  );
}

function AssetList({ label, assets, variant }) {
  if (!assets?.length) return null;
  const color = variant === "received" ? theme.colors.status.success : theme.colors.text.muted;
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{
        fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
        color, fontWeight: theme.fontWeight.heavy, marginBottom: 3,
        fontFamily: theme.fonts.display,
      }}>{label}</div>
      <ul style={{
        margin: 0, paddingLeft: 14,
        fontSize: 12, color: theme.colors.text.primary,
        fontFamily: theme.fonts.body, lineHeight: 1.45,
      }}>
        {assets.map((a, i) => <li key={i}>{formatAsset(a)}</li>)}
      </ul>
    </div>
  );
}

function formatAsset(a) {
  if (a.type === "player") {
    const meta = [a.position, a.team].filter(Boolean).join(" · ");
    return meta ? `${a.playerName} (${meta})` : a.playerName;
  }
  if (a.type === "pick") {
    return a.description || `${a.season} R${a.round}`;
  }
  if (a.type === "faab") {
    return `$${a.amount} FAAB`;
  }
  return JSON.stringify(a);
}

function DraftSubHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: 2.5, color: theme.colors.brand.accent,
      textTransform: "uppercase", fontWeight: theme.fontWeight.heavy,
      marginBottom: 10, paddingLeft: 2,
    }}>{children}</div>
  );
}

function DraftYearCard({ draft, earliestStatsYear, isFounding }) {
  // Founding (startup) draft defaults to expanded — it's the centerpiece
  // event in a dynasty league and worth showing up-front.
  const [open, setOpen] = useState(!!isFounding);
  const isAuction = draft.draftType === "auction";
  const isGridDraft = draft.draftType === "snake" || draft.draftType === "linear";
  const draftLabel = draft.draftType === "auction" ? "Auction"
                   : draft.draftType === "snake" ? "Snake"
                   : draft.draftType === "linear" ? "Linear"
                   : "Draft";
  const isDraftOnly = draft.statsCoverage === "draft_only";

  // Tier 2 — flag top-3 best + bottom-3 worst picks for the icon decoration.
  // Map by player+pick for the picks table renderer. Skipped when coverage
  // is draft_only (no analysis data exists).
  const top3Steals = new Set((draft.bestPicks || []).slice(0, 3).map(p => `${p.player}|${p.round}.${p.pick}`));
  const top3Busts = new Set((draft.worstPicks || []).slice(0, 3).map(p => `${p.player}|${p.round}.${p.pick}`));

  const columns = [
    { key: "rp",     label: "Pick",   render: (r) => `${r.round}.${String(r.pick).padStart(2, "0")}` },
    { key: "owner",  label: "Owner",  bold: true },
    { key: "player", label: "Player", render: (r) => {
      const key = `${r.player}|${r.round}.${r.pick}`;
      const isSteal = top3Steals.has(key);
      const isBust = top3Busts.has(key);
      return (
        <span>
          {r.player}
          {r.isKeeper && (
            <span style={{
              marginLeft: 6, padding: "1px 5px", borderRadius: 4,
              background: "rgba(34,197,94,0.18)", color: theme.colors.status.success,
              fontSize: 9, fontWeight: theme.fontWeight.heavy, letterSpacing: 1,
            }}>K</span>
          )}
          {isSteal && (
            <span
              title={`Steal — finished ${r.position}${r.seasonStats?.positionRank ?? "?"}, expected ${r.expectedPositionRank ?? "?"} (+${r.valueOverExpected})`}
              style={{ marginLeft: 6, color: "#fbbf24", fontSize: 12 }}
            >★</span>
          )}
          {isBust && (
            <span
              title={`Bust — finished ${r.position}${r.seasonStats?.positionRank ?? "?"}, expected ${r.expectedPositionRank ?? "?"} (${r.valueOverExpected})`}
              style={{ marginLeft: 6, color: "#ef4444", fontSize: 12 }}
            >✗</span>
          )}
          {r.seasonStats?.positionRank != null && !isSteal && !isBust && (
            <span style={{ marginLeft: 6, color: theme.colors.text.muted, fontSize: 10 }}>
              {r.position}{r.seasonStats.positionRank}
            </span>
          )}
        </span>
      );
    }},
    { key: "position", label: "Pos",   render: (r) => r.position || "—" },
    { key: "team",     label: "Team",  render: (r) => r.team || "—" },
    isAuction && { key: "cost", label: "$", align: "right", highlight: true,
                   render: (r) => r.cost != null ? `$${r.cost}` : "—" },
  ].filter(Boolean);
  return (
    <div style={{
      background: isFounding
        ? `linear-gradient(135deg, ${theme.colors.bg.raised} 0%, ${theme.colors.bg.surface} 100%)`
        : theme.colors.bg.raised,
      border: `1px solid ${isFounding ? theme.colors.brand.accent + "55" : theme.colors.border.default}`,
      borderRadius: 12,
      overflow: "hidden",
    }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 12,
          padding: "12px 16px", background: "transparent", border: "none",
          color: theme.colors.text.primary, cursor: "pointer", textAlign: "left",
          fontFamily: theme.fonts.display,
        }}
      >
        <span style={{ color: theme.colors.brand.accent, fontWeight: theme.fontWeight.black, width: 14 }}>
          {open ? "▾" : "▸"}
        </span>
        <span style={{ fontWeight: theme.fontWeight.heavy, fontSize: isFounding ? 19 : 17, minWidth: 60 }}>{draft.year}</span>
        {isFounding && (
          <span style={{
            fontSize: 10, letterSpacing: 2, textTransform: "uppercase",
            color: theme.colors.brand.accent, fontWeight: theme.fontWeight.heavy,
            padding: "2px 8px", borderRadius: 4,
            background: theme.colors.brand.accent + "18",
            border: `1px solid ${theme.colors.brand.accent}55`,
          }}>Founding Draft</span>
        )}
        <span style={{
          fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase",
          color: isAuction ? theme.colors.brand.accent : theme.colors.text.muted,
          fontWeight: theme.fontWeight.heavy,
        }}>{draftLabel}</span>
        {isDraftOnly && (
          <span
            title={draft.platform === 'yahoo'
              ? 'Player performance analysis is currently Sleeper-only. Yahoo player enrichment is on the roadmap. Picks still render normally.'
              : (earliestStatsYear
                  ? `Player performance data unavailable for pre-${earliestStatsYear} seasons. Picks render normally; no bust/steal/MVP analysis.`
                  : 'Player performance data unavailable for this season.')}
            style={{
              fontSize: 9, letterSpacing: 1.5, textTransform: "uppercase",
              color: theme.colors.text.muted, fontWeight: theme.fontWeight.heavy,
              padding: "2px 6px", borderRadius: 4,
              border: `1px solid ${theme.colors.border.default}`,
              cursor: "help",
            }}
          >Draft Only</span>
        )}
        <span style={{
          color: theme.colors.text.muted, fontSize: 12, fontFamily: theme.fonts.body,
          marginLeft: "auto",
        }}>
          {draft.totalPicks} pick{draft.totalPicks !== 1 ? "s" : ""}
          {draft.rounds ? ` · ${draft.rounds} round${draft.rounds !== 1 ? "s" : ""}` : ""}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 16px 16px" }}>
          {isDraftOnly && (
            <div style={{
              fontSize: 11, color: "#9ca3af", fontStyle: "italic",
              marginBottom: 10,
            }}>
              {draft.platform === 'yahoo'
                ? 'Player analysis is currently Sleeper-only. Yahoo player enrichment is on the roadmap.'
                : (earliestStatsYear
                    ? `Player analysis unavailable for pre-${earliestStatsYear} seasons (Sleeper stats coverage starts ${earliestStatsYear}).`
                    : 'Player analysis unavailable for this season.')}
            </div>
          )}
          {/* Snake/linear get the canonical board-room grid; auction stays
              in list view because auctions have no meaningful round
              structure to grid by. */}
          {isGridDraft ? (
            <DraftBoardGrid draft={draft} />
          ) : (
            <ResponsiveTable columns={columns} rows={draft.picks} emptyMessage="No picks recorded." />
          )}
        </div>
      )}
    </div>
  );
}
